import { useState } from 'react';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { DrawerV2, ButtonV2 } from '@bloomays-lib/ui.shared';
import { UPSERT_BLOOMER } from '@bloomays-lib/adapter.api-bloomer';
import { internationalPhoneRegex, mailRegex, nameRegex } from '@bloomays-lib/utils.shared';

import { cancel, notify } from '../../../../helpers/toastify';
import { FieldV2, InputV2, Row2, InfoV2 } from '../formFields';
import type { DashboardBloomer } from '../useMissionDashboard';

export type EditBloomerDrawerProps = {
  open: boolean;
  onClose: () => void;
  bloomer: NonNullable<DashboardBloomer>;
};

const NAME_ERROR = 'Min 1 lettre, sans emoji ni caractère spécial';
const MAIL_ERROR = 'Format requis : xxxx@xx.xx';
const PHONE_ERROR = 'Format requis : 0xxxxxxxxx ou +xxxxxxxxxxx';

/**
 * Édition de la personne Bloomer (profil) en panneau latéral (DrawerV2) via `UPSERT_BLOOMER`.
 * Couvre prénom / nom / email / email secondaire / téléphone — mêmes règles de validation que le
 * formulaire historique `UpdateBloomerForm` (regex partagées `utils.shared`). La pièce d'identité
 * (`credentialID`, optionnelle ici) reste gérée depuis le drawer « Documents ».
 */
export const EditBloomerDrawer = ({ open, onClose, bloomer }: EditBloomerDrawerProps) => {
  const { t } = useTranslation(['notify']);

  const [firstname, setFirstname] = useState(bloomer.firstname ?? '');
  const [lastname, setLastname] = useState(bloomer.lastname ?? '');
  const [email, setEmail] = useState(bloomer.email ?? '');
  const [secondaryEmail, setSecondaryEmail] = useState(bloomer.secondaryEmail ?? '');
  const [phoneNumber, setPhoneNumber] = useState(bloomer.phoneNumber ?? '');

  const [upsertBloomer, { loading }] = useMutation(UPSERT_BLOOMER);

  const trimmed = {
    firstname: firstname.trim(),
    lastname: lastname.trim(),
    email: email.trim(),
    secondaryEmail: secondaryEmail.trim(),
    phoneNumber: phoneNumber.trim(),
  };

  const errors = {
    firstname: trimmed.firstname.length < 1 || !nameRegex.test(trimmed.firstname) ? NAME_ERROR : undefined,
    lastname: trimmed.lastname.length < 1 || !nameRegex.test(trimmed.lastname) ? NAME_ERROR : undefined,
    email: trimmed.email.length < 1 || !mailRegex.test(trimmed.email) ? MAIL_ERROR : undefined,
    secondaryEmail: trimmed.secondaryEmail && !mailRegex.test(trimmed.secondaryEmail) ? MAIL_ERROR : undefined,
    phoneNumber: trimmed.phoneNumber && !internationalPhoneRegex.test(trimmed.phoneNumber) ? PHONE_ERROR : undefined,
  };
  const hasError = Object.values(errors).some(Boolean);

  const save = async () => {
    if (hasError) return;
    notify('info', t('updateInProgress', { ns: 'notify' }), null, { autoClose: 800, hideProgressBar: true });
    try {
      await upsertBloomer({
        variables: {
          input: {
            recordId: bloomer.recordId,
            firstname: trimmed.firstname,
            lastname: trimmed.lastname,
            email: trimmed.email,
            secondaryEmail: trimmed.secondaryEmail || undefined,
            phoneNumber: trimmed.phoneNumber || undefined,
          },
        },
      });
      notify('success', t('updateSuccess', { ns: 'notify' }));
      onClose();
    } catch (e: any) {
      cancel();
      notify('error', e.message || t('randomUpdateError', { ns: 'notify' }));
    }
  };

  return (
    <DrawerV2
      open={open}
      onClose={onClose}
      title="Bloomer"
      subtitle={bloomer.fullname || `${trimmed.firstname} ${trimmed.lastname}`.trim() || undefined}
      width={480}
      footer={
        <>
          <ButtonV2 variant="secondary" onClick={onClose}>
            Annuler
          </ButtonV2>
          <ButtonV2
            onClick={save}
            disabled={hasError}
            loading={loading}
            startIcon={<SaveRoundedIcon sx={{ fontSize: 15 }} />}
          >
            Enregistrer
          </ButtonV2>
        </>
      }
    >
      <Row2>
        <FieldV2 label="Prénom" required error={errors.firstname}>
          <InputV2 value={firstname} onChange={setFirstname} placeholder="Prénom" />
        </FieldV2>
        <FieldV2 label="Nom" required error={errors.lastname}>
          <InputV2 value={lastname} onChange={setLastname} placeholder="Nom" />
        </FieldV2>
      </Row2>
      <FieldV2 label="Email" required error={errors.email}>
        <InputV2 type="email" value={email} onChange={setEmail} placeholder="prenom.nom@exemple.com" />
      </FieldV2>
      <FieldV2 label="Email secondaire" error={errors.secondaryEmail}>
        <InputV2 type="email" value={secondaryEmail} onChange={setSecondaryEmail} placeholder="Optionnel" />
      </FieldV2>
      <FieldV2 label="Téléphone" error={errors.phoneNumber}>
        <InputV2 type="tel" value={phoneNumber} onChange={setPhoneNumber} placeholder="Optionnel" />
      </FieldV2>
      <InfoV2 tone="blue" icon={<BadgeRoundedIcon sx={{ fontSize: 16 }} />}>
        La pièce d'identité du Bloomer se gère depuis le panneau « Documents ».
      </InfoV2>
    </DrawerV2>
  );
};

export default EditBloomerDrawer;
